import {
  DEFAULT_SCENE_ANIMATION_NAMES,
  isSceneAnimationNameTaken,
} from "../blueprint/runtime/scene-animations.js";
import type { SceneBehaviorCatalog } from "./types.js";

/** Catalog sections that expose a user-visible unique name. */
export type SceneCatalogNameSection =
  | "animations"
  | "events"
  | "functions"
  | "variables"
  | "objects";

/**
 * Returns whether a catalog entry name is already taken in the given section
 * (trim + case-insensitive). Animation presets count as taken for `animations`.
 */
export function isSceneCatalogEntryNameTaken(
  section: SceneCatalogNameSection,
  name: string,
  catalog: SceneBehaviorCatalog | undefined,
  options?: { excludeEntryId?: string },
): boolean {
  if (section === "animations") {
    return isSceneAnimationNameTaken(name, catalog, options);
  }

  const normalized = String(name ?? "")
    .trim()
    .toLocaleLowerCase();
  if (!normalized) return false;

  const entries =
    section === "events"
      ? (catalog?.customEvents ?? [])
      : section === "functions"
        ? (catalog?.functions ?? [])
        : section === "objects"
          ? (catalog?.objectTypes ?? [])
          : (catalog?.variables ?? []);

  const excludeEntryId = options?.excludeEntryId;
  return entries.some(
    (entry) =>
      entry.id !== excludeEntryId &&
      entry.name.trim().toLocaleLowerCase() === normalized,
  );
}

/**
 * Allocates a name that does not collide with existing catalog entries
 * (and animation presets when section is `animations`).
 */
export function allocateUniqueCatalogName(
  section: SceneCatalogNameSection,
  baseName: string,
  catalog: SceneBehaviorCatalog | undefined,
): string {
  const trimmed = String(baseName ?? "").trim();
  const root = trimmed.length > 0 ? trimmed : "Entry";
  if (!isSceneCatalogEntryNameTaken(section, root, catalog)) {
    return root;
  }
  let index = 2;
  while (isSceneCatalogEntryNameTaken(section, `${root} ${index}`, catalog)) {
    index += 1;
  }
  return `${root} ${index}`;
}

/**
 * Lists default animation preset names (normalized) for conflict UI.
 */
export function getDefaultAnimationPresetNames(): readonly string[] {
  return DEFAULT_SCENE_ANIMATION_NAMES;
}
